<template>
    <modal id="custom-invoice-modal"
           v-model="showModal"
           :title="selectedUrl ? 'Edit Invoice' : 'Create New Invoice'"
           @submit="submitData"
           :loading="loading"
           :preloader="preloader">
        <form :data-url="selectedUrl ? selectedUrl : CUSTOM_INVOICES"
              method="POST"
              ref="form">

            <app-form-group
                label="Customer Name"
                type="text"
                v-model="formData.customer_name"
                placeholder="Enter customer name"
                :required="true"
                :error-message="$errorMessage(errors, 'customer_name')"
            />

            <app-form-group
                label="Mobile Number"
                type="text"
                v-model="formData.customer_mobile"
                placeholder="Enter mobile number"
                :required="true"
                :error-message="$errorMessage(errors, 'customer_mobile')"
            />

            <app-form-group
                label="Address"
                type="textarea"
                v-model="formData.customer_address"
                placeholder="Enter address"
                :required="false"
                :error-message="$errorMessage(errors, 'customer_address')"
            />

            <div class="row">
                <div class="col-md-6">
                    <app-form-group
                        label="Payment Method"
                        type="select"
                        v-model="formData.payment_method"
                        :list="[
                            {id: 'Cash', value: 'Cash'},
                            {id: 'bKash/Nagad', value: 'bKash/Nagad'},
                            {id: 'Bank Transfer', value: 'Bank Transfer'}
                        ]"
                        :required="true"
                        :error-message="$errorMessage(errors, 'payment_method')"
                    />
                </div>
                <div class="col-md-6">
                    <app-form-group
                        label="Discount"
                        type="number"
                        v-model="formData.discount"
                        placeholder="0.00"
                        :required="false"
                        :error-message="$errorMessage(errors, 'discount')"
                    />
                </div>
            </div>

            <!-- Dynamic Items Section -->
            <h5 class="mt-4 mb-3" style="font-size: 1.1rem; font-weight: 600; color: #495057;">Invoice Items</h5>
            
            <div class="invoice-items-container mb-4">
                <div v-for="(item, index) in formData.items" :key="index" class="row align-items-center mb-3">
                    <div class="col-md-7">
                        <div class="form-group mb-0">
                            <label v-if="index === 0" class="text-left d-block text-muted" style="font-size: 0.85rem; font-weight: 500;">Item Name / Description</label>
                            <input type="text" 
                                   v-model="item.name" 
                                   class="form-control" 
                                   placeholder="Enter item description" 
                                   required />
                            <small class="text-danger" v-if="errors['items.' + index + '.name']">
                                {{ errors['items.' + index + '.name'][0] }}
                            </small>
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div class="form-group mb-0">
                            <label v-if="index === 0" class="text-left d-block text-muted" style="font-size: 0.85rem; font-weight: 500;">Amount</label>
                            <input type="number" 
                                   v-model="item.amount" 
                                   class="form-control" 
                                   placeholder="0.00" 
                                   step="0.01" 
                                   min="0"
                                   required />
                            <small class="text-danger" v-if="errors['items.' + index + '.amount']">
                                {{ errors['items.' + index + '.amount'][0] }}
                            </small>
                        </div>
                    </div>
                    <div class="col-md-1 text-center">
                        <label v-if="index === 0" class="d-block mb-1">&nbsp;</label>
                        <button type="button" 
                                class="btn btn-outline-danger btn-sm mt-1 p-2" 
                                @click="removeItem(index)" 
                                :disabled="formData.items.length === 1">
                            <app-icon name="trash" style="width: 14px; height: 14px;" />
                        </button>
                    </div>
                </div>
                
                <div class="mt-3">
                    <button type="button" class="btn btn-outline-primary btn-sm" @click="addItem">
                        <app-icon name="plus" class="mr-1" style="width: 14px; height: 14px;" /> Add Item
                    </button>
                </div>
            </div>

            <!-- Summary Totals Row -->
            <div class="row justify-content-end">
                <div class="col-md-6">
                    <table class="table table-bordered table-sm text-right">
                        <tbody>
                            <tr>
                                <td class="font-weight-bold align-middle pr-3" style="width: 50%;">Subtotal</td>
                                <td class="pr-3 align-middle font-weight-bold">{{ calculatedSubtotal }}</td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold align-middle pr-3">Discount</td>
                                <td class="pr-3 align-middle">-{{ parseFloat(formData.discount || 0).toFixed(2) }}</td>
                            </tr>
                            <tr class="table-active">
                                <td class="font-weight-bold align-middle pr-3" style="font-size: 1.1rem;">Total Amount</td>
                                <td class="pr-3 align-middle font-weight-bold" style="font-size: 1.1rem; color: #007bff;">{{ calculatedTotal }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </form>
    </modal>
</template>

<script>
import FormHelperMixins from "../../../../common/Mixin/Global/FormHelperMixins";
import ModalMixin from "../../../../common/Mixin/Global/ModalMixin";
import { CUSTOM_INVOICES } from "../../../Config/ApiUrl";

export default {
    name: "CustomInvoiceCreateEditModal",
    mixins: [FormHelperMixins, ModalMixin],
    props: ['selectedUrl'],
    data() {
        return {
            CUSTOM_INVOICES,
            formData: {
                customer_name: '',
                customer_mobile: '',
                customer_address: '',
                project_name: '',
                amount: '',
                discount: 0,
                payment_method: 'Cash',
                items: [
                    { name: '', amount: '' }
                ]
            }
        }
    },
    computed: {
        calculatedSubtotal() {
            const items = this.formData.items || [];
            const subtotal = items.reduce((acc, item) => {
                return acc + (parseFloat(item.amount) || 0);
            }, 0);
            return subtotal.toFixed(2);
        },
        calculatedTotal() {
            const subtotal = parseFloat(this.calculatedSubtotal) || 0;
            const discount = parseFloat(this.formData.discount) || 0;
            return Math.max(0, subtotal - discount).toFixed(2);
        }
    },
    methods: {
        addItem() {
            this.formData.items.push({ name: '', amount: '' });
        },
        removeItem(index) {
            if (this.formData.items.length > 1) {
                this.formData.items.splice(index, 1);
            }
        },
        submitData() {
            const amount = parseFloat(this.calculatedSubtotal) || 0;
            this.formData.amount = amount;
            this.formData.project_name = (this.formData.items && this.formData.items[0]) ? this.formData.items[0].name : '';
            
            this.setBasicFormActionData()
                .save(this.formData);
        },
        afterSuccess({data}) {
            this.formData = {
                customer_name: '',
                customer_mobile: '',
                customer_address: '',
                project_name: '',
                amount: '',
                discount: 0,
                payment_method: 'Cash',
                items: [
                    { name: '', amount: '' }
                ]
            };
            $('#custom-invoice-modal').modal('hide');
            this.toastAndReload(data.message, 'custom-invoice-table');
            this.$emit('input', false);
        },
        afterSuccessFromGetEditData({data}) {
            this.preloader = false;
            if (!data.items || data.items.length === 0) {
                data.items = [{ name: data.project_name || '', amount: data.amount || '' }];
            }
            this.formData = data;
        }
    }
}
</script>
